"use client"

import {
  createContext,
  ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react"
import { useRouter, useSearchParams, usePathname } from "next/navigation"
import { useProjects, type Project } from "@/api/queries/projects"

const LS_KEY = "seedmancer_active_project_slug"

// Module-level ref used by the axios interceptor to read the active project ID
// without going through React rendering.
let _activeProjectId: string | null = null

export function getActiveProjectIdForAxios(): string | null {
  return _activeProjectId
}

interface ProjectContextProps {
  projects: Project[]
  activeProject: Project | null
  isLoading: boolean
  setActiveProject: (slug: string) => void
}

const ProjectContext = createContext<ProjectContextProps | null>(null)

// Inner component that reads useSearchParams — must live inside a Suspense
// boundary so Next.js can statically render pages that don't need it (e.g. 404).
function ProjectProviderInner({ children }: { children: ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { data: projects = [], isLoading } = useProjects()

  const slugFromUrl = searchParams.get("project")

  const activeProject = useMemo<Project | null>(() => {
    if (!projects.length) return null

    // 1. URL param
    if (slugFromUrl) {
      const found = projects.find((p) => p.slug === slugFromUrl)
      if (found) return found
    }

    // 2. localStorage
    try {
      const saved = localStorage.getItem(LS_KEY)
      if (saved) {
        const found = projects.find((p) => p.slug === saved)
        if (found) return found
      }
    } catch {
      // SSR / private browsing
    }

    // 3. Default project
    const def = projects.find((p) => p.slug === "default") ?? projects[0]
    return def ?? null
  }, [projects, slugFromUrl])

  // Keep module-level ref in sync
  const prevIdRef = useRef<string | null>(null)
  useEffect(() => {
    const id = activeProject?.id ?? null
    if (id !== prevIdRef.current) {
      _activeProjectId = id
      prevIdRef.current = id
    }
  }, [activeProject])

  // Persist slug to localStorage when it changes
  useEffect(() => {
    if (!activeProject) return
    try {
      localStorage.setItem(LS_KEY, activeProject.slug)
    } catch {
      // ignore
    }
  }, [activeProject])

  const setActiveProject = useCallback(
    (slug: string) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set("project", slug)
      router.push(`${pathname}?${params.toString()}`)
    },
    [router, pathname, searchParams],
  )

  return (
    <ProjectContext.Provider value={{ projects, activeProject, isLoading, setActiveProject }}>
      {children}
    </ProjectContext.Provider>
  )
}

export function ProjectProvider({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={null}>
      <ProjectProviderInner>{children}</ProjectProviderInner>
    </Suspense>
  )
}

export function useProjectContext(): ProjectContextProps {
  const ctx = useContext(ProjectContext)
  if (!ctx) throw new Error("useProjectContext must be used within a ProjectProvider")
  return ctx
}
